import { ArrowRight, ShieldCheck, ReceiptText, Clock3 } from "lucide-react";
import { SITE } from "@/lib/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1646324554833-f0b6a479fa5d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXIlMjB3b3JraW5nJTIwb24lMjBzaXRlfGVufDB8fHx8MTc4MDk5MzM2MHww&ixlib=rb-4.1.0&q=85";

const REASSURANCE = [
  { icon: ShieldCheck, text: "Sans engagement" },
  { icon: ReceiptText, text: "100% déductible des charges" },
  { icon: Clock3, text: "Réponse sous 24h" },
];

export default function Hero() {
  return (
    <section
      id="accueil"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-end overflow-hidden bg-[#1A2E4A]"
    >
      {/* Background image */}
      <img
        src={HERO_IMG}
        alt="Artisan en plein travail sur un chantier"
        className="absolute inset-0 w-full h-full object-cover object-center"
        loading="eager"
      />
      {/* Overlay */}
      <div className="absolute inset-0 bg-[#1A2E4A]/85" />
      <div className="absolute inset-0 grain-overlay opacity-40" />
      {/* Side accent line */}
      <div className="hidden md:block absolute left-0 top-0 bottom-0 w-1 bg-[#E8611A]" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-12 sm:pt-40 sm:pb-16">
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-sm bg-white/10 border border-white/15 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-[#E8611A]" />
            <span className="text-white/80 text-xs font-medium tracking-wide uppercase">
              Assistance administrative · BTP · {SITE.zone}
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="font-display font-extrabold text-white text-4xl sm:text-5xl lg:text-7xl leading-[1.05] tracking-tight"
          >
            Arrêtez de perdre vos soirées sur des{" "}
            <span className="text-[#E8611A]">devis</span> et des{" "}
            <span className="text-[#E8611A]">factures</span>.
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-6 text-white/85 text-base sm:text-lg lg:text-xl max-w-2xl leading-relaxed"
          >
            On s&apos;occupe de toute votre gestion administrative pendant que vous
            êtes sur le chantier. Devis, facturation, relances, paperasse — vous
            ne touchez plus à rien.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-3 sm:gap-4">
            <a
              href="#services"
              data-testid="hero-cta-primary"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-sm bg-[#E8611A] text-white font-semibold hover:bg-[#E8611A]/90 transition-all hover:-translate-y-0.5 shadow-lg shadow-[#E8611A]/20"
            >
              Voir les formules
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href={SITE.calendly}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-secondary"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-sm border-2 border-white/80 text-white font-semibold hover:bg-white hover:text-[#1A2E4A] transition-colors"
            >
              Prendre rendez-vous
            </a>
          </div>

          {/* Reassurance band */}
          <div
            data-testid="hero-reassurance"
            className="mt-12 sm:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 max-w-4xl"
          >
            {REASSURANCE.map(({ icon: Icon, text }, i) => (
              <div
                key={i}
                className="flex items-center gap-3 px-4 py-3 rounded-sm bg-white/5 border border-white/10 backdrop-blur-sm"
              >
                <span className="inline-flex items-center justify-center w-8 h-8 rounded-sm bg-[#E8611A]/15 text-[#E8611A]">
                  <Icon className="w-4 h-4" strokeWidth={2.4} />
                </span>
                <span className="text-white/90 text-sm font-medium">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
